import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { of } from 'rxjs';
import * as addressActions from '../actions/addresses.action';
import * as custumorActions from '../actions/customer.action';
import { switchMap, map, catchError, withLatestFrom, tap, take, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { AddressesService } from 'src/app/shop/services';
import { getCustomerAccessToken, getCustomerDefaultAddress } from '../selectors';
import { getAddressesPageInfo } from '../selectors/addresses.selector';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { getRouterState, Go } from 'src/app/store';
import { UpdateCartBuyerIdentity } from '../actions';
import { getActiveLanguageNoCulture } from '@teamfoster/sdk/language-ngrx';
import * as exemptionActions from '../actions/tax-exemptions.action';

@Injectable()
export class AddressesEffects {
  constructor(
    private actions$: Actions,
    private addressesService: AddressesService,
    private store: Store,
    private router: Router,
    private sb: MatSnackBar
  ) {}

  GetAddresses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.LoadAddresses),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([, token, lang]) =>
        this.addressesService.CustomerGetAddresses(token?.accessToken ?? '', undefined, undefined, lang).pipe(
          map(addresses => addressActions.LoadAddressesSuccess({ addresses })),
          catchError(errors => of(addressActions.LoadAddressesFail({ errors })))
        )
      )
    );
  });

  LoadMoreAddresses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.LoadMoreAddresses),
      withLatestFrom(
        this.store.select(getCustomerAccessToken),
        this.store.select(getAddressesPageInfo),
        this.store.select(getActiveLanguageNoCulture)
      ),
      switchMap(([{}, token, pageInfo, lang]) =>
        this.addressesService.CustomerGetAddresses(token?.accessToken ?? '', pageInfo?.cursor, undefined, lang).pipe(
          map(addresses => addressActions.LoadAddressesSuccess({ addresses })),
          catchError(errors => of(addressActions.LoadAddressesFail({ errors })))
        )
      )
    );
  });

  AddAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.AddAddress),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ address, redirect }, token, lang]) =>
        this.addressesService.CustomerAddressCreate(token?.accessToken || '', address, lang).pipe(
          map(result => addressActions.AddAddressSuccess({ address: result, redirect })),
          catchError(errors => of(addressActions.AddAddressFail({ errors })))
        )
      )
    );
  });

  UpdateAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.UpdateAddress),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ address, id }, token, lang]) => {
        return this.addressesService.CustomerAddressUpdate(token?.accessToken || '', address, id, lang).pipe(
          map(result => addressActions.UpdateAddressSuccess({ address: result.address, id: result.id })),
          catchError(errors => of(addressActions.UpdateAddressFail({ errors })))
        );
      })
    );
  });

  UpdateAddressSuccess2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.UpdateAddressSuccess),
      switchMap(a =>
        this.store
          .select(getRouterState)
          .pipe(take(1))
          .pipe(
            map(({ state }) => {
              this.sb.open('Adres geupdated', 'OK', { panelClass: 'snackbar-success' });

              custumorActions.LoadCustomer();
              return Go({ path: ['/account'] });
            })
          )
      )
      //map(a => custumorActions.LoadCustomer())
    );
  });

  UpdateAddressSuccess3$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.UpdateAddressSuccess),
      map(a => exemptionActions.ResetTaxExemptionsCheck())
    );
  });

  DeleteAddress$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.DeleteAddress),
      withLatestFrom(this.store.select(getCustomerAccessToken), this.store.select(getActiveLanguageNoCulture)),
      switchMap(([{ id, redirect }, token, lang]) => {
        return this.addressesService.CustomerAddressDelete(token?.accessToken || '', id, lang).pipe(
          map(a => addressActions.DeleteAddressSuccess({ id: id, redirect })),
          catchError(errors => of(addressActions.DeleteAddressFail({ errors })))
        );
      })
    );
  });

  DeleteAddressSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(addressActions.DeleteAddressSuccess),
        tap(a => {
          if (a.redirect?.length) {
            this.router.navigate(a.redirect);
          }
          this.sb.open('Adres verwijderd', 'OK');
        })
      );
    },
    { dispatch: false }
  );

  DeleteAddressSuccess2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.DeleteAddressSuccess),
      map(a => custumorActions.LoadCustomer())
    );
  });

  DeleteAddressSuccess3$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.DeleteAddressSuccess),
      map(a => exemptionActions.ResetTaxExemptionsCheck())
    );
  });

  // AddAddressSuccessssss$ = createEffect(
  //   () => {
  //     return this.actions$.pipe(
  //       ofType(addressActions.AddAddressSuccess),
  //       tap(a => {
  //         if (a.redirect?.length) {
  //           this.router.navigate(a.redirect);
  //         }
  //         this.sb.open('Adres opgeslagen', 'OK');
  //       })
  //     );
  //   },
  //   { dispatch: false }
  // );

  AddAddressSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.AddAddressSuccess),
      switchMap(a =>
        this.store
          .select(getRouterState)
          .pipe(take(1))
          .pipe(
            map(({ state }) => {
              this.sb.open('Adres opgeslagen', 'OK');

              return Go({ path: state.queryParams['returnUrl'] ? [state.queryParams['returnUrl']] : a.redirect || [state.url] });
            })
          )
      )
    );
  });

  AddAddressSuccess2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.AddAddressSuccess),
      map(a => custumorActions.LoadCustomer())
    );
  });

  AddAddressSuccess3$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.AddAddressSuccess),
      map(a => exemptionActions.ResetTaxExemptionsCheck())
    );
  });

  updateAddressSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.UpdateAddressSuccess),
      withLatestFrom(this.store.select(getCustomerDefaultAddress), this.store.select(getCustomerAccessToken)),
      filter(([{ address }, defaultAddress, token]) => address.id.split('?')[0] === defaultAddress?.id.split('?')[0]),
      map(([{ address }, defaultAddress, token]) => UpdateCartBuyerIdentity({ token: token?.accessToken ?? '' }))
    );
  });

  updateAddressSuccess2$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(addressActions.UpdateAddressSuccess),
      withLatestFrom(this.store.select(getCustomerDefaultAddress), this.store.select(getCustomerAccessToken)),
      filter(([{ address }, defaultAddress, token]) => address.id.split('?')[0] === defaultAddress?.id.split('?')[0]),
      map(([{ address }, defaultAddress, token]) => custumorActions.LoadCustomer())
    );
  });
}
